import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, User, Lock, AlertCircle, Loader2, Eye, EyeOff, Mail, KeyRound, RefreshCw } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { login as loginService, verifyMfaCode, resendMfaCode, getErrorMessage, resendVerification, ApiError, isMfaPendingResponse, isDirectAuthResponse } from '../services/authService';
import { validateEmail } from '../utils/validation';

type LoginStep = 'credentials' | 'mfa';

const Login: React.FC = () => {
  // Step 1: Credentials
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Step 2: MFA
  const [currentStep, setCurrentStep] = useState<LoginStep>('credentials');
  const [mfaCode, setMfaCode] = useState('');
  const [pendingMfaToken, setPendingMfaToken] = useState('');
  const [obfuscatedEmail, setObfuscatedEmail] = useState('');
  const [codeExpiresAt, setCodeExpiresAt] = useState<Date | null>(null);
  const [remainingResends, setRemainingResends] = useState(3);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [trustedDeviceExpirationDays, setTrustedDeviceExpirationDays] = useState(30);

  // Common state
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string>(() =>
    new URLSearchParams(window.location.search).get('reason') === 'idle'
      ? 'You were signed out after 15 minutes of inactivity. Please sign in again.'
      : ''
  );
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string; code?: string }>({});
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [verificationResent, setVerificationResent] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const navigate = useNavigate();
  const { login: setAuthUser } = useAuth();
  const mfaInputRef = useRef<HTMLInputElement>(null);

  // Fetch security settings on mount
  useEffect(() => {
    const fetchSecuritySettings = async () => {
      try {
        const response = await fetch('/api/settings/security');
        if (response.ok) {
          const data = await response.json();
          if (data.trustedDeviceExpirationDays) {
            setTrustedDeviceExpirationDays(data.trustedDeviceExpirationDays);
          }
        }
      } catch (error) {
        // Use default value if fetch fails
        console.debug('Could not fetch security settings, using defaults');
      }
    };
    fetchSecuritySettings();
  }, []);

  // Countdown timer for MFA code expiration
  useEffect(() => {
    if (codeExpiresAt) {
      const timer = setInterval(() => {
        const now = new Date();
        const remaining = Math.max(0, Math.floor((codeExpiresAt.getTime() - now.getTime()) / 1000));
        setTimeRemaining(remaining);

        if (remaining === 0) {
          clearInterval(timer);
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [codeExpiresAt]);

  // Focus MFA input when step changes
  useEffect(() => {
    if (currentStep === 'mfa' && mfaInputRef.current) {
      mfaInputRef.current.focus();
    }
  }, [currentStep]);

  const validateCredentialsForm = (): boolean => {
    const errors: { email?: string; password?: string } = {};

    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      errors.email = emailValidation.error;
    }

    if (!password) {
      errors.password = 'Password is required';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateMfaForm = (): boolean => {
    const errors: { code?: string } = {};

    if (!mfaCode) {
      errors.code = 'Verification code is required';
    } else if (!/^\d{6}$/.test(mfaCode)) {
      errors.code = 'Code must be 6 digits';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setEmailNotVerified(false);
    setVerificationResent(false);

    if (!validateCredentialsForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await loginService({ email, password });

      // Check if MFA is required or if login completed directly
      if (isDirectAuthResponse(response)) {
        // MFA is disabled - login completed, redirect to dashboard
        setAuthUser(response.user);
        navigate('/dashboard');
      } else if (isMfaPendingResponse(response)) {
        // MFA is enabled - store MFA data and move to step 2
        setPendingMfaToken(response.pendingMfaToken);
        setObfuscatedEmail(response.obfuscatedEmail);
        setCodeExpiresAt(new Date(response.codeExpiresAt));
        setRemainingResends(3); // Reset resend count for new MFA session
        setCurrentStep('mfa');
      }
    } catch (err) {
      const apiError = err as ApiError;
      if (apiError.statusCode === 403 && apiError.message?.toLowerCase().includes('email')) {
        setEmailNotVerified(true);
        setError('Please verify your email address before signing in.');
      } else {
        const errorMessage = getErrorMessage(err);
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setResendSuccess(false);

    if (!validateMfaForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await verifyMfaCode({
        code: mfaCode,
        pendingMfaToken: pendingMfaToken,
        rememberMe: rememberDevice
      });

      // Update auth context and redirect
      setAuthUser(response.user);
      navigate('/dashboard');
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      // Clear the code input on error
      setMfaCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendMfaCode = async () => {
    setIsResending(true);
    setError('');
    setResendSuccess(false);

    try {
      const response = await resendMfaCode(pendingMfaToken);

      if (response.success && response.newPendingMfaToken) {
        setPendingMfaToken(response.newPendingMfaToken);
        if (response.codeExpiresAt) {
          setCodeExpiresAt(new Date(response.codeExpiresAt));
        }
        setRemainingResends(response.remainingResends);
        setMfaCode('');
        setResendSuccess(true);
      } else {
        setError(response.message || 'Failed to resend code');
      }
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  const handleBackToCredentials = () => {
    // Force a page reload to ensure clean state - fixes MFA loop bug
    window.location.href = '/login';
  };

  const handleResendVerification = async () => {
    setIsResendingVerification(true);
    try {
      await resendVerification({ email });
      setVerificationResent(true);
    } catch (err) {
      setVerificationResent(true);
    } finally {
      setIsResendingVerification(false);
    }
  };

  const handleMfaCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setMfaCode(value);
    if (fieldErrors.code) {
      setFieldErrors({ ...fieldErrors, code: undefined });
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-gray-800/20 via-gray-700/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-black via-gray-900/50 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-gradient-to-br from-gray-900/10 to-gray-800/10 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <Navigation variant="dark" />

      {/* Content */}
      <div className="relative z-10 flex items-center justify-center px-4 py-8 pt-32" style={{minHeight: 'calc(100vh - 120px)'}}>
        <div className="w-full max-w-md">
          {/* Welcome Text */}
          <div className="text-center mb-8">
            <h1 className="text-5xl font-black mb-4 text-white">
              {currentStep === 'credentials' ? 'Welcome Back' : 'Verify Your Identity'}
            </h1>
            <p className="text-xl text-slate-400">
              {currentStep === 'credentials'
                ? 'Sign in to access your dashboard'
                : `Enter the code sent to ${obfuscatedEmail}`}
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
            {/* Error Alert */}
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-200">{error}</p>
                  {emailNotVerified && !verificationResent && (
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={isResendingVerification}
                      className="mt-2 inline-flex items-center text-sm text-white hover:text-gray-300 transition-colors font-medium disabled:opacity-50"
                    >
                      {isResendingVerification ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-1" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4 mr-1" />
                          Resend verification email
                        </>
                      )}
                    </button>
                  )}
                  {verificationResent && (
                    <p className="mt-2 text-sm text-green-400">
                      Verification email sent! Check your inbox and spam folder.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Success message for resend */}
            {resendSuccess && (
              <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-start space-x-3">
                <Mail className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-200">A new verification code has been sent to your email.</p>
              </div>
            )}

            {currentStep === 'credentials' ? (
              // Step 1: Credentials Form
              <form onSubmit={handleCredentialsSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (fieldErrors.email) {
                        setFieldErrors({ ...fieldErrors, email: undefined });
                      }
                    }}
                    disabled={isLoading}
                    className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#AD0000]/50 focus:ring-2 focus:ring-[#AD0000]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{borderColor: fieldErrors.email ? 'rgba(239, 68, 68, 0.5)' : undefined}}
                    placeholder="Enter your email"
                  />
                  {fieldErrors.email && (
                    <p className="mt-2 text-sm text-red-400">{fieldErrors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">
                    <Lock className="w-4 h-4 inline mr-2" />
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (fieldErrors.password) {
                          setFieldErrors({ ...fieldErrors, password: undefined });
                        }
                      }}
                      disabled={isLoading}
                      className="w-full px-4 py-3 pr-12 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#AD0000]/50 focus:ring-2 focus:ring-[#AD0000]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{borderColor: fieldErrors.password ? 'rgba(239, 68, 68, 0.5)' : undefined}}
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed p-1 rounded-lg bg-white/5 hover:bg-white/10"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  {fieldErrors.password && (
                    <p className="mt-2 text-sm text-red-400">{fieldErrors.password}</p>
                  )}
                </div>

                <div className="flex items-center justify-end text-sm">
                  <Link
                    to="/forgot-password"
                    className="text-white hover:text-gray-300 transition-colors font-medium"
                  >
                    Forgot password?
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="group w-full px-6 py-4 bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 hover:scale-105 rounded-xl transition-all duration-250 ease-spring shadow-xl shadow-red-500/30 hover:shadow-glow-red-lg flex items-center justify-center space-x-2 font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-xl"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Signing In...</span>
                    </>
                  ) : (
                    <>
                      <span>Continue</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-250" />
                    </>
                  )}
                </button>
              </form>
            ) : (
              // Step 2: MFA Verification Form
              <form onSubmit={handleMfaSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">
                    <KeyRound className="w-4 h-4 inline mr-2" />
                    Verification Code
                  </label>
                  <input
                    ref={mfaInputRef}
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    value={mfaCode}
                    onChange={handleMfaCodeChange}
                    disabled={isLoading}
                    className="w-full px-4 py-4 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#AD0000]/50 focus:ring-2 focus:ring-[#AD0000]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-center text-2xl tracking-[0.5em] font-mono"
                    style={{borderColor: fieldErrors.code ? 'rgba(239, 68, 68, 0.5)' : undefined}}
                    placeholder="000000"
                  />
                  {fieldErrors.code && (
                    <p className="mt-2 text-sm text-red-400">{fieldErrors.code}</p>
                  )}
                </div>

                {/* Remember this device checkbox */}
                <div className="flex items-center">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rememberDevice}
                      onChange={(e) => setRememberDevice(e.target.checked)}
                      disabled={isLoading}
                      className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#AD0000] focus:ring-[#AD0000] focus:ring-offset-0"
                    />
                    <span className="ml-2 text-slate-400">Remember this device for {trustedDeviceExpirationDays} days</span>
                  </label>
                </div>

                {/* Timer and Resend */}
                <div className="flex items-center justify-between text-sm">
                  <div className="text-slate-400">
                    {timeRemaining > 0 ? (
                      <>Code expires in <span className="text-white font-medium">{formatTime(timeRemaining)}</span></>
                    ) : (
                      <span className="text-red-400">Code expired</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleResendMfaCode}
                    disabled={isResending || remainingResends === 0}
                    className="inline-flex items-center text-white hover:text-gray-300 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isResending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Resend ({remainingResends})
                      </>
                    )}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || mfaCode.length !== 6}
                  className="group w-full px-6 py-4 bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 hover:scale-105 rounded-xl transition-all duration-250 ease-spring shadow-xl shadow-red-500/30 hover:shadow-glow-red-lg flex items-center justify-center space-x-2 font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-xl"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <>
                      <span>Verify & Sign In</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-250" />
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleBackToCredentials}
                  className="w-full flex items-center justify-center space-x-2 text-slate-400 hover:text-white transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back to login</span>
                </button>
              </form>
            )}

            {currentStep === 'credentials' && (
              <div className="mt-8 text-center">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/10"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white/5 text-slate-400">
                      New to Auto Access Solutions?
                    </span>
                  </div>
                </div>
                <div className="mt-6">
                  <Link
                    to="/register"
                    className="text-white hover:text-gray-300 transition-colors font-bold"
                  >
                    Create your account →
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Login;
